/* eslint-disable max-len */
import * as React from 'react';
import { useRef } from 'react';
import { Pencil, Trash2, Plus, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import dayjs from 'dayjs';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/evo/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/evo/ui/alert-dialog';
import { Button } from '@/components/evo/ui/button';
import { Input } from '@/components/evo/ui/input';
import { Label } from '@/components/evo/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { cn } from '@/components/evo/lib/utils';
import { useColumnResize } from '@/hooks/evo/use-column-resize';
import { ResizeHandle } from '@/components/evo/ui/resize-handle';
import { ProductMergeDefault, MergeType, UserNameMap } from './types';

type SortField = 'stockCodeContains' | 'mergeType' | 'createdBy' | 'updatedBy' | 'changed';
type SortDirection = 'asc' | 'desc' | null;

interface ProductMergeDefaultsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: ProductMergeDefault[];
  onAdd?: (stockCodeContains: string, mergeType: MergeType) => void;
  onEdit?: (item: ProductMergeDefault, stockCodeContains: string, mergeType: MergeType) => void;
  onDelete?: (item: ProductMergeDefault) => void;
  isLoading?: boolean;
  userNameMap?: UserNameMap;
}

export function ProductMergeDefaultsModal({
  open,
  onOpenChange,
  data,
  onAdd,
  onEdit,
  onDelete,
  isLoading = false,
  userNameMap = {},
}: ProductMergeDefaultsModalProps) {
  const [visibleItems, setVisibleItems] = React.useState<ProductMergeDefault[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const ITEMS_PER_PAGE = 10;

  const [sortField, setSortField] = React.useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(null);

  // Ref for table element
  const tableRef = useRef<HTMLTableElement | null>(null);

  // Column resize functionality using the shared hook
  const columnKeys = ['stockCodeContains', 'mergeType', 'createdBy', 'updatedBy', 'changed', 'actions'];
  const { columnWidths, handleMouseDown, handleDoubleClick, setTableRef } = useColumnResize({
    tableId: 'product-merge-defaults-grid',
    columns: columnKeys,
    defaultWidths: {
      stockCodeContains: 150,
      mergeType: 130,
      createdBy: 150,
      updatedBy: 150,
      changed: 120,
      actions: 100,
    },
    minWidth: 80,
    maxWidth: 400,
  });

  const [formOpen, setFormOpen] = React.useState(false);
  const [formMode, setFormMode] = React.useState<'add' | 'edit'>('add');
  const [editingItem, setEditingItem] = React.useState<ProductMergeDefault | null>(null);
  const [stockCodeContains, setStockCodeContains] = React.useState('');
  const [mergeType, setMergeType] = React.useState<MergeType>(MergeType.MergeByQuantity);

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [itemToDelete, setItemToDelete] = React.useState<ProductMergeDefault | null>(null);

  const getChangedDate = (item: ProductMergeDefault): string => {
    return item.updated ?? item.created ?? '';
  };

  const sortedData = React.useMemo(() => {
    if (!sortField || !sortDirection) return data;

    return [...data].sort((a, b) => {
      let aValue: string | null = '';
      let bValue: string | null = '';

      switch (sortField) {
        case 'stockCodeContains':
          aValue = a.stockCodeContains;
          bValue = b.stockCodeContains;
          break;
        case 'mergeType':
          aValue = a.mergeType ?? '';
          bValue = b.mergeType ?? '';
          break;
        case 'createdBy':
          aValue = a.createdBy || 'System';
          bValue = b.createdBy || 'System';
          break;
        case 'updatedBy':
          aValue = a.updatedBy || '';
          bValue = b.updatedBy || '';
          break;
        case 'changed':
          aValue = getChangedDate(a);
          bValue = getChangedDate(b);
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDirection]);

  React.useEffect(() => {
    if (sortedData.length > 0) {
      setVisibleItems(sortedData.slice(0, ITEMS_PER_PAGE));
      setHasMore(sortedData.length > ITEMS_PER_PAGE);
    } else {
      setVisibleItems([]);
      setHasMore(false);
    }
  }, [sortedData]);

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollPercentage =
        (target.scrollTop + target.clientHeight) / target.scrollHeight;

      if (scrollPercentage > 0.8 && hasMore) {
        const currentLength = visibleItems.length;
        const nextItems = sortedData.slice(0, currentLength + ITEMS_PER_PAGE);
        setVisibleItems(nextItems);
        setHasMore(nextItems.length < sortedData.length);
      }
    },
    [sortedData, hasMore, visibleItems.length],
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = dayjs(dateString);
    if (!date.isValid()) return dateString;
    return date.format('DD/MM/YYYY');
  };

  const getUserName = (userId: string | null): string => {
    if (!userId) return 'System';
    return userNameMap[userId] || userId;
  };

  const handleAddClick = () => {
    setFormMode('add');
    setStockCodeContains('');
    setMergeType(MergeType.MergeByQuantity);
    setEditingItem(null);
    setFormOpen(true);
  };

  const handleEditClick = (item: ProductMergeDefault) => {
    setFormMode('edit');
    setStockCodeContains(item.stockCodeContains);
    setMergeType(item.mergeType ?? MergeType.MergeByQuantity);
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleDeleteClick = (item: ProductMergeDefault) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formMode === 'add') {
      console.log('Add new Product Merge Default:', { stockCodeContains, mergeType });
      if (onAdd) {
        onAdd(stockCodeContains, mergeType);
      }
    } else if (formMode === 'edit' && editingItem) {
      console.log('Edit Product Merge Default:', { id: editingItem.id, stockCodeContains, mergeType });
      if (onEdit) {
        onEdit(editingItem, stockCodeContains, mergeType);
      }
    }

    setFormOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      console.log('Delete Product Merge Default:', {
        id: itemToDelete.id,
        stockCodeContains: itemToDelete.stockCodeContains,
        mergeType: itemToDelete.mergeType,
      });
      if (onDelete) {
        onDelete(itemToDelete);
      }
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleStockCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 50) {
      setStockCodeContains(value);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[70vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-2xl font-semibold">
              Product Merge Defaults
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 pb-6 flex flex-col" style={{ height: 'calc(70vh - 80px)' }}>
            <div className="flex justify-end mb-3 flex-shrink-0">
              <Button
                size="sm"
                onClick={handleAddClick}
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add New
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden flex-1 min-h-0">
              <div className="h-full overflow-auto" onScroll={handleScroll}>
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-muted-foreground">Loading...</span>
                  </div>
                ) : (
                  <table
                    ref={(el) => {
                      tableRef.current = el;
                      setTableRef(el);
                    }}
                    className="w-full caption-bottom text-sm"
                  >
                    <thead className="[&_tr]:border-b bg-background sticky top-0 z-10">
                      <tr className="border-b transition-colors">
                        <th
                          className="text-foreground px-2 text-left align-middle font-medium relative group bg-white dark:bg-gray-950"
                          style={{ minWidth: columnWidths.stockCodeContains || 150 }}
                        >
                          <div className="flex items-center h-10">
                            <button
                              onClick={() => handleSort('stockCodeContains')}
                              className="flex items-center gap-1 hover:text-primary transition-colors flex-1 justify-between px-2 min-w-0"
                            >
                              <span className="font-semibold truncate">StockCode Contains</span>
                              {sortField === 'stockCodeContains' ? (
                                sortDirection === 'asc' ? (
                                  <ArrowUp className="h-4 w-4 stroke-[2]" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 stroke-[2]" />
                                )
                              ) : (
                                <ArrowUpDown className="h-4 w-4 stroke-[2] opacity-40" />
                              )}
                            </button>
                          </div>
                          <ResizeHandle
                            onMouseDown={(e) => handleMouseDown(e, 'stockCodeContains')}
                            onDoubleClick={(e) => handleDoubleClick(e, 'stockCodeContains', tableRef.current)}
                          />
                        </th>
                        <th
                          className="text-foreground px-2 text-left align-middle font-medium relative group bg-white dark:bg-gray-950"
                          style={{ minWidth: columnWidths.mergeType || 130 }}
                        >
                          <div className="flex items-center h-10">
                            <button
                              onClick={() => handleSort('mergeType')}
                              className="flex items-center gap-1 hover:text-primary transition-colors flex-1 justify-between px-2 min-w-0"
                            >
                              <span className="font-semibold truncate">Merge Type</span>
                              {sortField === 'mergeType' ? (
                                sortDirection === 'asc' ? (
                                  <ArrowUp className="h-4 w-4 stroke-[2]" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 stroke-[2]" />
                                )
                              ) : (
                                <ArrowUpDown className="h-4 w-4 stroke-[2] opacity-40" />
                              )}
                            </button>
                          </div>
                          <ResizeHandle
                            onMouseDown={(e) => handleMouseDown(e, 'mergeType')}
                            onDoubleClick={(e) => handleDoubleClick(e, 'mergeType', tableRef.current)}
                          />
                        </th>
                        <th
                          className="text-foreground px-2 text-left align-middle font-medium relative group bg-white dark:bg-gray-950"
                          style={{ minWidth: columnWidths.createdBy || 150 }}
                        >
                          <div className="flex items-center h-10">
                            <button
                              onClick={() => handleSort('createdBy')}
                              className="flex items-center gap-1 hover:text-primary transition-colors flex-1 justify-between px-2 min-w-0"
                            >
                              <span className="font-semibold truncate">Created By</span>
                              {sortField === 'createdBy' ? (
                                sortDirection === 'asc' ? (
                                  <ArrowUp className="h-4 w-4 stroke-[2]" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 stroke-[2]" />
                                )
                              ) : (
                                <ArrowUpDown className="h-4 w-4 stroke-[2] opacity-40" />
                              )}
                            </button>
                          </div>
                          <ResizeHandle
                            onMouseDown={(e) => handleMouseDown(e, 'createdBy')}
                            onDoubleClick={(e) => handleDoubleClick(e, 'createdBy', tableRef.current)}
                          />
                        </th>
                        <th
                          className="text-foreground px-2 text-left align-middle font-medium relative group bg-white dark:bg-gray-950"
                          style={{ minWidth: columnWidths.updatedBy || 150 }}
                        >
                          <div className="flex items-center h-10">
                            <button
                              onClick={() => handleSort('updatedBy')}
                              className="flex items-center gap-1 hover:text-primary transition-colors flex-1 justify-between px-2 min-w-0"
                            >
                              <span className="font-semibold truncate">Updated By</span>
                              {sortField === 'updatedBy' ? (
                                sortDirection === 'asc' ? (
                                  <ArrowUp className="h-4 w-4 stroke-[2]" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 stroke-[2]" />
                                )
                              ) : (
                                <ArrowUpDown className="h-4 w-4 stroke-[2] opacity-40" />
                              )}
                            </button>
                          </div>
                          <ResizeHandle
                            onMouseDown={(e) => handleMouseDown(e, 'updatedBy')}
                            onDoubleClick={(e) => handleDoubleClick(e, 'updatedBy', tableRef.current)}
                          />
                        </th>
                        <th
                          className="text-foreground px-2 text-left align-middle font-medium relative group bg-white dark:bg-gray-950"
                          style={{ minWidth: columnWidths.changed || 120 }}
                        >
                          <div className="flex items-center h-10">
                            <button
                              onClick={() => handleSort('changed')}
                              className="flex items-center gap-1 hover:text-primary transition-colors flex-1 justify-between px-2 min-w-0"
                            >
                              <span className="font-semibold truncate">Changed</span>
                              {sortField === 'changed' ? (
                                sortDirection === 'asc' ? (
                                  <ArrowUp className="h-4 w-4 stroke-[2]" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 stroke-[2]" />
                                )
                              ) : (
                                <ArrowUpDown className="h-4 w-4 stroke-[2] opacity-40" />
                              )}
                            </button>
                          </div>
                          <ResizeHandle
                            onMouseDown={(e) => handleMouseDown(e, 'changed')}
                            onDoubleClick={(e) => handleDoubleClick(e, 'changed', tableRef.current)}
                          />
                        </th>
                        <th
                          className="text-foreground px-2 text-right align-middle font-medium relative group bg-white dark:bg-gray-950"
                          style={{ minWidth: columnWidths.actions || 100 }}
                        >
                          <div className="h-10 flex items-center justify-end px-2">
                            <span className="font-semibold">Actions</span>
                          </div>
                          <ResizeHandle
                            onMouseDown={(e) => handleMouseDown(e, 'actions')}
                            onDoubleClick={(e) => handleDoubleClick(e, 'actions', tableRef.current)}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="[&_tr:last-child]:border-0">
                      {visibleItems.length === 0 ? (
                        <tr className="hover:bg-muted/50 border-b transition-colors">
                          <td
                            colSpan={6}
                            className="p-2 align-middle h-24 text-center text-muted-foreground"
                          >
                            No product merge defaults found.
                          </td>
                        </tr>
                      ) : (
                        visibleItems.map((item) => {
                          const isSystem = !item.createdBy;
                          return (
                            <tr key={item.id} className="hover:bg-muted/50 border-b transition-colors">
                              <td className="p-2 align-middle font-medium whitespace-nowrap">
                                {item.stockCodeContains}
                              </td>
                              <td className="p-2 align-middle whitespace-nowrap">
                                {item.mergeType ? (
                                  <span
                                    className={cn(
                                      'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
                                      item.mergeType === MergeType.MergeByQuantity &&
                                        'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/30',
                                      item.mergeType === MergeType.MergeByUnitPrice &&
                                        'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/30',
                                      item.mergeType === MergeType.NoMerge &&
                                        'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/30',
                                    )}
                                  >
                                    {item.mergeType}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-2 align-middle whitespace-nowrap">
                                <span
                                  className={cn(
                                    isSystem && 'font-semibold text-primary',
                                  )}
                                >
                                  {getUserName(item.createdBy)}
                                </span>
                              </td>
                              <td className="p-2 align-middle whitespace-nowrap">
                                {item.updatedBy ? getUserName(item.updatedBy) : '-'}
                              </td>
                              <td className="p-2 align-middle whitespace-nowrap font-mono text-xs">
                                {formatDate(getChangedDate(item))}
                              </td>
                              <td className="p-2 align-middle whitespace-nowrap text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEditClick(item)}
                                    className="h-8 w-8"
                                    title="Edit"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteClick(item)}
                                    className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
                {hasMore && visibleItems.length > 0 && !isLoading && (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    Scroll for more...
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'add' ? 'Add New Product Merge Default' : 'Edit Product Merge Default'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFormSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="stockCodeContains">
                  StockCode Contains
                  <span className="text-xs text-muted-foreground ml-2">
                    ({stockCodeContains.length}/50)
                  </span>
                </Label>
                <Input
                  id="stockCodeContains"
                  value={stockCodeContains}
                  onChange={handleStockCodeChange}
                  placeholder="Enter stock code pattern"
                  maxLength={50}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mergeType">Merge Type</Label>
                <Select
                  value={mergeType}
                  onValueChange={(value: string) => setMergeType(value as MergeType)}
                >
                  <SelectTrigger id="mergeType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MergeType.MergeByQuantity}>
                      {MergeType.MergeByQuantity}
                    </SelectItem>
                    <SelectItem value={MergeType.MergeByUnitPrice}>
                      {MergeType.MergeByUnitPrice}
                    </SelectItem>
                    <SelectItem value={MergeType.NoMerge}>
                      {MergeType.NoMerge}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {formMode === 'add' ? 'Add' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product merge default?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
